import { createSupportLanguageApi, updateSupportLanguageApi } from "@/api/services/global/master";
import { TGetSupportLanguageListOutputDto } from "@/api/services/global/master/dtos/get-master-support-language-list.dto";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { masterDataSupportLanguageSchema } from "@/features/master-data/schema/masterDataSupportLanguageSchema";
import { TMasterSupportLanguageForm } from "@/features/master-data/types/masterDataSupportLanguageForm";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";

type TSupportLanguageFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
  selectedSupportLanguage?: TGetSupportLanguageListOutputDto;
};

export const SupportLanguageFormModal: FC<TSupportLanguageFormModalProps> = ({ onClose, isOpen, selectedSupportLanguage }) => {
  const queryClient = useQueryClient();

  const defaultValues = useMemo(
    () => ({
      supportLanguageName: selectedSupportLanguage ? selectedSupportLanguage.supportLanguageName : undefined,
      mainCountry: selectedSupportLanguage ? selectedSupportLanguage.mainCountry : undefined,
    }),
    [selectedSupportLanguage],
  );

  const methods = useForm<TMasterSupportLanguageForm>({
    resolver: yupResolver(masterDataSupportLanguageSchema),
    mode: "onBlur",
    defaultValues,
  });

  const { control } = methods;

  const onSubmit = async (data: TMasterSupportLanguageForm) => {
    try {
      if (selectedSupportLanguage) {
        const rs = await updateSupportLanguageApi({
          supportLanguages: [
            {
              id: toNumber(selectedSupportLanguage.id),
              supportLanguageName: data.supportLanguageName,
              mainCountry: data.mainCountry ?? null,
            },
          ],
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      } else {
        const rs = await createSupportLanguageApi({
          supportLanguageName: data.supportLanguageName,
          mainCountry: data.mainCountry ?? null,
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_SUPPORT_LANGUAGE] });
      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal
      width={640}
      title={selectedSupportLanguage ? "編集" : "追加"}
      isOpen={isOpen}
      enableFooter={false}
      onClose={onClose}
      enableCloseIcon={false}
    >
      <Form methods={methods} onSubmit={onSubmit} schema={masterDataSupportLanguageSchema}>
        <Stack gap={3} my={3}>
          <Grid item xs={12}>
            <FormField control={control} name="supportLanguageName">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="mainCountry">
              <Input />
            </FormField>
          </Grid>
        </Stack>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="submit">保存</Button>
        </Stack>
      </Form>
    </Modal>
  );
};
